import * as React from 'react';
import s from './PlansGrid.scss';
import {
  AdvancedPlansFeature,
  AdvancedPlansFeatureSection,
  AdvancedPlansProductData,
  FeaturesGroupSection,
} from 'types';
import { ProductHeader } from './GridHeader/ProductHeader';
import { FeatureRow } from './FeatureRow';
import classNames from 'classnames';
import { PlanSections } from '../../Sections/PlanSections';
import { RootStore, withInjectedRootStore } from 'providers';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common';
import { ShowMoreSectionsButton } from '../../Sections/ShowMoreSectionsButton';
import { Collapse, useScrollDetection } from '@wix/package-picker-components';
import { UseGridSections } from './UseGridSections';
import { useTranslation } from 'react-i18next';
import { useStickyHeaderHeight } from 'providers/StickyHeaderHeightProvider';
import { useResizeObserver } from '../../../../hooks/useResizeObserver';
import { Group } from './Group';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { Experiments, MediaDevices } from 'enums';
import { useBreakpoint } from 'providers/BreakpointProvider';

export interface PlansGridProps extends InjectedExperimentsProps {
  products: AdvancedPlansProductData[];
  features: AdvancedPlansFeature[];
  onClickSelect(productData: AdvancedPlansProductData): void;
  sections: AdvancedPlansFeatureSection[];
  groups: FeaturesGroupSection[];
  rootStore: RootStore;
}

export const PlansGridComp: React.FC<PlansGridProps> = ({
  products,
  features,
  groups,
  onClickSelect,
  sections,
  rootStore,
  experiments,
}) => {
  const gridRef = React.useRef<HTMLDivElement>(null);
  const gridHeaderRef = React.useRef<HTMLDivElement>(null);
  const [isGridHeaderSticky, setGridHeaderSticky] = React.useState(false);
  const [gridHeaderHeight, setGridHeaderHeight] = React.useState(0);
  const [areSectionsFullyOpened, setSectionsFullyOpened] =
    React.useState(false);
  const { headerHeight } = useStickyHeaderHeight();
  const { openedSections, foldedSections, toggleSections, areSectionsFolded } =
    UseGridSections(sections, rootStore.appStore);
  const { isJapanesePackagePicker } = rootStore.appStore;
  const { t } = useTranslation();

  const gridCustomProperties = {
    '--headerHeight': `${headerHeight}px`,
    '--gridHeaderHeight': `${gridHeaderHeight}px`,
    '--gridColumns': products.length,
  } as React.CSSProperties;

  const handleGridHeaderResize = (entry: ResizeObserverEntry) => {
    const { height } = entry.target.getBoundingClientRect();
    if (height !== gridHeaderHeight) {
      setGridHeaderHeight(height);
    }
  };

  useResizeObserver({
    ref: gridHeaderRef,
    callback: handleGridHeaderResize,
    dep: [gridHeaderRef.current],
  });

  useScrollDetection({
    ref: gridRef,
    offset: headerHeight,
    cb: setGridHeaderSticky,
  });

  const shouldHaveFeaturesGroups =
    rootStore.appStore.isDayful ||
    experiments.enabled(Experiments.FeaturesGroupingPlansGrid);

  const { currentBreakpoint } = useBreakpoint();

  const tooltipsAsASubtitle =
    isJapanesePackagePicker && currentBreakpoint === MediaDevices.Desktop;
  const isMediumTableViewMode = rootStore.appStore.isMediumSizeTableView;

  return (
    <div
      className={classNames(s.root, {
        [s.plansGridMediumView]: isMediumTableViewMode,
      })}
    >
      <div
        className={classNames(s.grid, { [s.japan]: tooltipsAsASubtitle })}
        style={gridCustomProperties}
        ref={gridRef}
      >
        <div
          className={classNames(s.header, {
            [s.sticky]: isGridHeaderSticky,
          })}
        >
          <div
            className={classNames(s.deadCell, s.productHeader, {
              [s.productHeaderScaled]: isGridHeaderSticky,
            })}
            ref={gridHeaderRef}
          />
          {products.map((product) => (
            <ProductHeader
              isSticky={isGridHeaderSticky}
              key={product.id}
              product={product}
              onClickSelect={onClickSelect}
            />
          ))}
        </div>
        {features.map((feature, idx) => (
          <FeatureRow key={idx} products={products} feature={feature} />
        ))}
        {shouldHaveFeaturesGroups &&
          groups?.map((group, index) => (
            <Group
              data-testid='group'
              key={index}
              group={group}
              products={products}
            ></Group>
          ))}
        {!!sections.length &&
          openedSections.map((section) => (
            <PlanSections
              key={section.translationKey}
              section={section}
              products={products}
              sectionsAreFullyVisible={areSectionsFullyOpened}
            />
          ))}
        <Collapse
          className={s.sectionsCollapse}
          isOpened={areSectionsFolded}
          onRest={({ isFullyOpened }) => setSectionsFullyOpened(isFullyOpened)}
          onWork={() => setSectionsFullyOpened(false)}
        >
          {foldedSections.map((section) => (
            <PlanSections
              key={section.translationKey}
              section={section}
              products={products}
              sectionsAreFullyVisible={areSectionsFullyOpened}
            />
          ))}
        </Collapse>
      </div>
      {!!sections.length && (
        <ShowMoreSectionsButton
          suffixIcon={areSectionsFolded ? <ChevronUp /> : <ChevronDown />}
          onClick={toggleSections}
        >
          {t(
            areSectionsFolded
              ? 'Advanced.Plans_General_Show_less'
              : 'Advanced.Plans_General_Show_all_features',
          )}
        </ShowMoreSectionsButton>
      )}
    </div>
  );
};

export const PlansGrid = withExperiments(withInjectedRootStore(PlansGridComp));
